import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';

const SaveChatModal = forwardRef(({ 
  isOpen, 
  onClose, 
  onSuccess, 
  chatId,
  chatTitle = "Your chat is being saved"
}, ref) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [completed, setCompleted] = useState(false);

  useImperativeHandle(ref, () => ({
    showSuccessState: () => {
      setShowSuccess(true);
      
      setTimeout(() => {
        setCompleted(true);
        setTimeout(() => {
          onSuccess?.(chatId);
        }, 1500); // Show success for 1.5s then trigger onSuccess
      }, 600); // Show success animation for 0.6s
    }
  }));

  useEffect(() => {
    if (isOpen) {
      setShowSuccess(false);
      setCompleted(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="save-chat-modal">
      <div className="modal-overlay" onClick={onClose} />
      
      <div className="phone-container">
        <div className="content">
          <div className="loading-container">
            <h1 className="loading-title">
              {completed ? 'Chat saved!' : chatTitle}
            </h1>
            
            {!showSuccess ? (
              <>
                <div className="loading-spinner" id="loadingSpinner">
                  <div className="spinner-ring"></div>
                </div>
                
                <div className="loading-message" id="loadingMessage">
                  Saving your conversation<span className="loading-dots"></span>
                </div>
              </>
            ) : (
              <>
                <div className="success-container show" id="successContainer">
                  <div className="success-circle">
                    <svg className="success-checkmark" viewBox="0 0 24 24">
                      <path className="checkmark-path" d="m9 12 2 2 4-4"/>
                    </svg>
                  </div>
                </div>
                
                <div className="success-message show" id="successMessage">
                  Chat saved successfully!
                </div>
              </>
            )}
          </div>
        </div>
        
        <div className="safe-area"></div>
      </div>

      <style>{`
        * { margin:0; padding:0; box-sizing:border-box; }

        .save-chat-modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 10000;
        }

        .modal-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
        }

        .phone-container{
          width:100%; height:100vh;
          background:linear-gradient(135deg,#f9fff9 0%,#e6f4ea 50%,#a6cdb1 100%);
          overflow:hidden; position:relative;
          display: flex;
          flex-direction: column;
        }

        .content{ flex: 1; overflow-y:auto; scrollbar-width:none; -ms-overflow-style:none; display: flex; align-items: center; justify-content: center; }
        .content::-webkit-scrollbar{ display:none; }

        /* Loading container */
        .loading-container {
          text-align: center;
          padding: clamp(32px, 8vh, 64px);
          max-width: 400px;
          width: 100%;
        }

        .loading-title {
          font-size: clamp(24px, 5vw, 32px);
          font-weight: 700;
          color: #12332f;
          line-height: 1.25;
          margin-bottom: clamp(32px, 6vh, 48px);
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.8s ease forwards;
        }

        /* Loading spinner */
        .loading-spinner {
          width: clamp(60px, 12vw, 80px);
          height: clamp(60px, 12vw, 80px);
          margin: 0 auto clamp(24px, 4vh, 32px);
          position: relative;
          opacity: 0;
          animation: fadeInUp 0.8s ease 0.3s forwards;
        }

        .spinner-ring {
          width: 100%;
          height: 100%;
          border: 4px solid rgba(18, 51, 47, 0.1);
          border-top: 4px solid #12332f;
          border-radius: 50%;
          animation: spin 1.2s linear infinite;
        }

        /* Success checkmark */
        .success-container {
          width: clamp(60px, 12vw, 80px);
          height: clamp(60px, 12vw, 80px);
          margin: 0 auto clamp(24px, 4vh, 32px);
          position: relative;
        }

        .success-circle {
          width: 100%;
          height: 100%;
          border: 4px solid #22c55e;
          border-radius: 50%;
          background: #22c55e;
          display: flex;
          align-items: center;
          justify-content: center;
          transform: scale(0);
          animation: successPop 0.6s ease forwards;
        }

        .success-checkmark {
          width: 80%;
          height: 80%;
          stroke: white;
          stroke-width: 3;
          stroke-linecap: round;
          stroke-linejoin: round;
          fill: none;
        }

        .checkmark-path {
          stroke-dasharray: 20;
          stroke-dashoffset: 20;
          animation: drawCheck 0.6s ease 0.3s forwards;
        }

        /* Loading message */
        .loading-message {
          font-size: clamp(16px, 3vw, 18px);
          color: #6d6d70;
          line-height: 1.5;
          opacity: 0;
          transform: translateY(10px);
          animation: fadeInUp 0.8s ease 0.6s forwards;
        }

        .success-message {
          font-size: clamp(16px, 3vw, 18px);
          color: #22c55e;
          font-weight: 600;
          line-height: 1.5;
          opacity: 0;
          transform: translateY(10px);
        }

        /* Success state animations */
        .success-container.show {
          opacity: 1;
          transform: translateY(0);
          animation: fadeInUp 0.8s ease forwards;
        }

        .success-message.show {
          opacity: 1;
          transform: translateY(0);
          animation: fadeInUp 0.8s ease 0.3s forwards;
        }

        /* Animations */
        @keyframes fadeInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes successPop {
          0% { transform: scale(0); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }

        @keyframes drawCheck {
          to {
            stroke-dashoffset: 0;
          }
        }

        @keyframes fadeInSuccess {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Pulsing dots for loading text */
        .loading-dots {
          display: inline-block;
        }

        .loading-dots::after {
          content: '';
          animation: loadingDots 1.5s infinite;
        }

        @keyframes loadingDots {
          0%, 20% { content: ''; }
          40% { content: '.'; }
          60% { content: '..'; }
          80%, 100% { content: '...'; }
        }

        .safe-area{ display: none; }
      `}</style>
    </div>
  );
});

SaveChatModal.displayName = 'SaveChatModal';

export default SaveChatModal;